import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { BestPracticesIcons } from '@deltek/specification-client/core/Widgets/BestPracticesSetting/BestPracticesIcons'
import { makeStyles, Theme } from '@material-ui/core/styles'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import { useSelectedProjectElementNode } from 'components/WorkspaceEditor/Workspace2Store'
import React, { memo, useMemo } from 'react'
import { ContentItemViewNodesType } from '../../../types/contentItemViewNode.type'
import { BestPracticeSyncAction } from '../../smartProjectCopy/types'
import { BP_STYLING, BP_TEST_IDS } from '../constants'
import { useBestPracticesData } from '../hooks/useBestPracticesData'
import { useBestPracticesDisplay } from '../hooks/useBestPracticesDisplay'
import { useBestPracticesSyncStatus } from '../hooks/useBestPracticesSyncStatus'
const useStyles = makeStyles((theme: Theme) => ({
    container: {
        display: 'inline-flex',
        alignItems: 'center',
        marginLeft: BP_STYLING.ICON_MARGIN_LEFT,
        verticalAlign: 'middle',
        '& .bpIconContainer': {
            display: 'inline-flex',
            alignItems: 'center',
        },
    },
    hidden: {
        display: 'none',
    },
    BPIcons: {
        minWidth: theme.spacing(BP_STYLING.ICON_MIN_WIDTH_SPACING),
        lineHeight: 0,
        fontSize: pxToRem(BP_STYLING.ICON_FONT_SIZE_PX),
        textAlign: 'center',
        color: BP_STYLING.ICON_COLOR,
        position: 'absolute',
        right: -theme.spacing(2),
        paddingTop: theme.spacing(BP_STYLING.ICON_PADDING_TOP_SPACING),
        zIndex: BP_STYLING.ICON_Z_INDEX,
    },
}))

interface BestPracticesIconWrapperProps {
    /** The content item to display BP icons for */
    contentItem: Partial<ContentItemViewNodesType>
    /** Additional CSS class names */
    className?: string
    /** Project element node ID for sync checking */
    projectElementNodeId?: string
}

/**
 * BestPracticesIconWrapper - Displays Best Practices icons for tree view content items
 *
 * This component:
 * - Checks if BP icons should be displayed based on permissions and content type
 * - Fetches BP status from the content item
 * - Renders the BestPracticesIcons component from the shared package
 *
 * @example
 * <BestPracticesIconWrapper contentItem={node} />
 */
export const BestPracticesIconWrapper = memo<BestPracticesIconWrapperProps>(
    ({ contentItem, className, projectElementNodeId }) => {
        const classes = useStyles()
        const selectedProjectElementNode = useSelectedProjectElementNode()
        // Check if Smart Project Copy is enabled
        const isSmartProjectCopyEnabled = useMemo(
            () => processEnv.REACT_APP_EnableSmartProjectCopy === '1',
            []
        )

        // Check if BP icons should be displayed
        const { shouldDisplayBPIcon } = useBestPracticesDisplay(contentItem)

        // Get BP status data
        const { isBestPractice, isGlobalBestPractice } = useBestPracticesData(
            contentItem
        )

        // Get sync status (only when Smart Project Copy is enabled)
        const {
            syncActionType,
            isBPOutOfSync,
            isGBPOutOfSync,
            isLoading,
            handleSync,
        } = useBestPracticesSyncStatus({
            contentItem,
            projectElementNodeId,
            enabled: isSmartProjectCopyEnabled && shouldDisplayBPIcon,
        })

        // Don't render if display is disabled or no BP is set
        if (
            !shouldDisplayBPIcon ||
            (!isBestPractice && !isGlobalBestPractice)
        ) {
            return null
        }

        // Determine if BP is synced (action is NONE means in sync)
        const isBestPracticeSynced =
            isBestPractice &&
            syncActionType.action === BestPracticeSyncAction.None

        const isGlobalBestPracticeSynced =
            isGlobalBestPractice &&
            syncActionType.action === BestPracticeSyncAction.None
        if (!selectedProjectElementNode?.isInProject) return <></>
        return (
            <span
                className={clsx(classes.container, className, classes.BPIcons)}
                data-testid={BP_TEST_IDS.ICON_WRAPPER}
            >
                {isSmartProjectCopyEnabled ? (
                    <BestPracticesIcons
                        isBestPractice={isBestPractice}
                        isGlobalBestPractice={isGlobalBestPractice}
                        isBestPracticeOutOfSync={isBPOutOfSync}
                        isGlobalBestPracticeOutOfSync={isGBPOutOfSync}
                        isBestPracticeSyncing={isLoading && isBestPractice}
                        isGlobalBestPracticeSyncing={
                            isLoading && isGlobalBestPractice
                        }
                        isBestPracticeSynced={isBestPracticeSynced}
                        isGlobalBestPracticeSynced={isGlobalBestPracticeSynced}
                        handleOutOfSyncIconClick={handleSync}
                        syncActionType={syncActionType}
                    />
                ) : (
                    <BestPracticesIcons
                        isBestPractice={isBestPractice}
                        isGlobalBestPractice={isGlobalBestPractice}
                    />
                )}
            </span>
        )
    }
)

BestPracticesIconWrapper.displayName = 'BestPracticesIconWrapper'
